import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAreYouSure } from "components/areYouSure";
import BreadCrumbs from "components/breadcrumbs";
import {
  Button,
  IconButton,
  NoStyleA,
  OutlineButton,
  SubtleButton,
} from "components/button";
import DataLabel from "components/dataLabel";
import Dropdown from "components/dropdown";
import InlineEdit from "components/inline_edit";
import { useModalContext } from "components/modal";
import SafeArea from "components/safe-area";
import { useSnackbarContext } from "components/snackbar";
import Spinner from "components/spinner";
import CustomerContainer from "containers/customer";
import LineItemsContainer from "containers/lineItems";
import { useAuthContext } from "contexts/auth";
import { format } from "date-fns";
import Switch from "rc-switch";
import { RiMore2Line } from "react-icons/ri";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useHistory, useParams } from "react-router-dom";
import { TInvoice } from "types/invoice";
import { dollarFormatter } from "utils";
import NewPaymentModal from "views/payment/newPaymentModal";
import { Status } from "components/status";
import TaxModal from "views/requests/components/taxModal";
import { MarkInvoiceAsSentModal } from "./markInvoiceAsSentModal";
import PayableOptions from "./payable-options/payable-options";
import PaymentContainer from "./payment";
import SendInvoiceModal from "./sendinvoiceModal";
import clients from "constants/clients";
import domains from "constants/domains";
import quickbooksDesktopEndpoints from "constants/routes/quickbooks/desktop";
import constructUrl from "utils/constructUrl";
import AppointmentTimes from "./appointment-times";
import AppointmentStatuses from "../../containers/appointmentCard/appointmentStatuses";
import AppointmentCardMini from "containers/appointmentCardMini";
import GenerateInvoicePDFModal from "./GenerateInvoicePDFModal";
import { useTranslation } from "react-i18next";
import { enUS, es } from "date-fns/locale";
import convertCVD  from "utils/convertCVD";
import InvoiceProductionAllocation from "./production-allocation";
import { getContrastTextColorRGB } from "utils/getContrastTextColor";
import ResendInvoiceModal from "./resendInvoiceModal";
import MultiInput from "components/input/multi";
import SendReceiptModal  from "./SendReceiptModal";

export interface INewInvoicePayConfig {
  is_payable: boolean;
  allow_card: boolean;
  allow_ach: boolean;
}

export default function Invoice() {
  const { t, i18n } = useTranslation();
  const tBase = "views.invoice.index";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const locale = i18n.language === "es" ? es : enUS;

  const { id } = useParams<{ id: string }>(),
    areYouSure = useAreYouSure(),
    history = useHistory(),
    snackBar = useSnackbarContext(),
    queryClient = useQueryClient(),
    modal = useModalContext();
  
  const { auth, cvd_type } = useAuthContext();
  const [customerEmails, setCustomerEmails] = useState<string[]>([]);

  console.log("invoice id: ", id);

  const query = useQuery<TInvoice>(
    ["invoice", id],
    async () =>
      await axios
        .get(
          `
            ${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/${id}
          `
        )
        .then((res) => res.data)
  );

  const {
    refetch: fetchPdfExists,
    data: pdfExists,
    isFetching: checkingPdf,
  } = useQuery(
    ["invoice-pdf", id],
    () =>
      axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/pdf-exists/${id}`
        )
        .then((res) => res.data),
    {
      enabled: false,   
      retry: false,  
    }
  );

  
  useEffect(() => {
    if (query.data?.customer?.email) {
      const baseEmail = query.data?.customer?.email;
      const additional = query.data?.customer?.additional_emails || [];
      setCustomerEmails([baseEmail, ...additional]);
    }
  }, [query.data]);

  const statusColor = query && query.data && query.data.status && convertCVD(query.data.status.color, cvd_type);

  console.log("query.data", query.data);
  
  const handleResend = () => {
    if (!query.data) return;
    modal.setModal({
      component: <ResendInvoiceModal customer={query.data.customer} id={id} />,
      label: tr("Resend Invoice"),
    });
  };

  const sendInvoice = useMutation(
    async () =>
      await axios
        .post(
          `
  ${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/${id}/resend
  `
        )
        .then((res) => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        snackBar.showSnackbar(tr("Invoice sent!"));
      },
    }
  );

  const updatePayOptions = useMutation(
    async (newConfig: INewInvoicePayConfig) =>
      await axios
        .put(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/${id}/payable`,
          {
            ...newConfig,
          }
        )
        .then((res) => res.data),
    {
      onMutate: async (newConfig: INewInvoicePayConfig) => {
        await queryClient.cancelQueries(["invoice", id]);

        const prev = queryClient.getQueryData(["invoice", id]);

        queryClient.setQueryData(["invoice", id], (old: any) => ({
          ...old,
          ...newConfig,
        }));

        return { prev };
      },
      onError: (err, _, context: any) => {
        queryClient.setQueryData(["invoice", id], context.prev);
      },
    }
  );

  const handleUpdatePayOptions = async (newConfig: INewInvoicePayConfig) => {
    await updatePayOptions.mutateAsync(newConfig);
  };

  const updateQBSync = useMutation(
    async (e: boolean) =>
      await axios
        .post(
          `
  ${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/${id}/qbsync?status=${e}
  `
        )
        .then((res) => res.data),
    {
      onMutate: async (e) => {
        await queryClient.cancelQueries(["invoice", id]);

        const prev = queryClient.getQueryData(["invoice", id]);

        queryClient.setQueryData(["invoice", id], (old: any) => ({
          ...old,
          qb_id: e ? null : "disabled",
        }));

        return { prev };
      },
      onError: (err, _, context: any) => {
        queryClient.setQueryData(["invoice", id], context.prev);
      },
    }
  );

  const deleteInvoice = useMutation(
    async () =>
      await axios
        .delete(
          `
  ${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/${id}
  `
        )
        .then((res) => res.data),
    {
      onSuccess: () => {
        history.goBack();
        snackBar.showSnackbar(tr("Invoice deleted!"));
      },
    }
  );
  const markInvoiceAsPaid = useMutation(
    async () =>
      await axios
        .post(
          `
  ${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/${id}/mark_as_paid
  `
        )
        .then((res) => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        snackBar.showSnackbar(tr("Invoice marked as paid!"));
      },
    }
  );

  const handleClickAddToSyncLog = async () => {
    const client = clients.QUICKBOOKS;
    const domain = domains.DESKTOP;
    const endpoint = quickbooksDesktopEndpoints.SYNC_LOG;

    try {
      const response = await axios.post(
        constructUrl({ client, domain, endpoint }),
        {
          action: "add_or_modify",
          resource_type: "Invoice",
          resource_id: id,
        }
      );

      if (response.data) {
        snackBar.showSnackbar(tr("Added to sync log!"));
      }
    } catch (error) {
      snackBar.showSnackbar(tr("Error adding to sync log!"), error);
    }
  };

  const resyncInvoice = async () => {
    await axios.post(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/sync-invoice`,
      { invoiceId: id }
    );
  };

  const markInvoiceAsPending = useMutation(
    async () =>
      await axios
        .post(
          `
  ${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/${id}/mark_as_pending
  `
        )
        .then((res) => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        snackBar.showSnackbar(tr("Invoice marked as pending!"));
      },
    }
  );

  if (query.isLoading) return <Spinner />;
  if (!query.data) return null;

  console.log(query.data.payments)

  console.log(Array.isArray(query.data?.payments))
  console.log(Array.isArray(query.data?.payments) && query.data.payments.length > 0)
  

  const status =
    query.data.status?.id === 17
      ? "open"
      : query.data.status?.id === 18
        ? "paid"
        : query.data.status?.id === 37
          ? "pending"
          : "unpaid";

  const {
    is_payable: isPayable,
    allow_card: allowCard,
    allow_ach: allowAch,
    archive_date: archiveDate,
  } = query.data || {};

  // Check if invoice is archived
  console.log("archiveDate: ", archiveDate);
  const isArchived = !!archiveDate;

  console.log("muh data: ", query.data);
  console.log("isArchived: ", isArchived);

  // Early return for archived invoice view
  if (isArchived) {
    return (
      <div className="divide-y">
        <SafeArea>
          <div className="flex justify-between py-4">
            <div>
              <BreadCrumbs link={[{ label: tr("Invoice"), to: "/invoices" }]} />
              <div className="text-baseline">
                <span className="text-3xl">
                  {tr("Invoice")} #{query.data?.id}
                </span>
                <span className="mx-1 text-xl font-light text-slate-600">
                  {tr("for")}
                </span>
                <NoStyleA to={`/customers/${query.data?.customer?.id}`}>
                  <span className="text-xl font-medium text-slate-800">
                    {query.data?.customer?.name}
                  </span>
                </NoStyleA>
                <Status color={statusColor || "128,128,128"} marginLeft="0.5rem">
                  {tr(query.data?.status?.label || "Unknown")}
                </Status>
                <Status color="rgb(156, 163, 175)" marginLeft="0.5rem">
                  {tr("Archived")}
                </Status>
              </div>
            </div>
            {/* <div className="flex space-x-2">
              <Dropdown
                reference={
                  <IconButton square>
                    <RiMore2Line />
                  </IconButton>
                }
                data={[
                  {
                    button: [
                      {
                        label: tr("Invoice PDF"),
                        onClick: () => {
                          modal.setModal({
                            component: (
                              <GenerateInvoicePDFModal
                                invoiceId={id}
                                emails={
                                  query.data?.customer.email
                                    ? [query.data?.customer.email]
                                    : null
                                }
                              />
                            ),
                            label: tr("Invoice PDF"),
                          });
                        },
                      },
                      status === "paid" && {
                        label: tr("Send Paid Receipt"),
                        onClick: async () => {
                          const { data } = await fetchPdfExists();
                          if (data.exists) {
                            modal.setModal({
                              label: tr("Send Paid Receipt"),
                              component: (
                                <SendReceiptModal
                                  invoiceId={id}
                                  tr={tr}
                                  customerEmails={customerEmails}
                                  setCustomerEmails={setCustomerEmails}
                                />
                              ),
                            });
                          } else {
                            modal.setModal({
                              component: (
                                <GenerateInvoicePDFModal
                                  invoiceId={id}
                                  emails={
                                    query.data?.customer.email
                                      ? [query.data?.customer.email]
                                      : null
                                  }
                                  isReceipt={true}
                                />
                              ),
                              label: tr("Send Paid Receipt"),
                            });
                          }
                        }
                      },
                    ],
                  },
                ]}
              />
            </div> */}
          </div>
        </SafeArea>
        <SafeArea>
          <div className={`py-4 space-y-8`}>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    {tr("Invoice Archived")}
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      {tr("This invoice has been archived and cannot receive payments. Payment options have been disabled.")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="flex items-center p-6 py-10 space-x-8 rounded-lg border"
              style={{
                background: `rgba(${statusColor || "128,128,128"},0.15)`,
                color: `rgb(${statusColor || "128,128,128"})`,
              }}
            >
              <div className="flex flex-col">
                <span>{tr("Invoice Total")}</span>
                <span className="text-xl lg:text-5xl md:text-4xl">
                  {dollarFormatter(query.data?.actual_total)}
                </span>
              </div>
              <div className="text-xl lg:text-5xl md:text-4xl">-</div>
              <div className="flex flex-col">
                <span>{tr("Paid")}</span>
                <span className="text-xl lg:text-5xl md:text-4xl">
                  {dollarFormatter(query.data?.paid_total)}
                </span>
              </div>
              <div className="text-xl lg:text-5xl md:text-4xl">=</div>
              <div className="flex flex-col">
                <span>{tr("Due")}</span>
                <span className="text-xl lg:text-5xl md:text-4xl">
                  {dollarFormatter(query.data?.amount_due)}
                </span>
              </div>
            </div>
            <div className="divide-y">
              <div className="py-4">
                <h2>{tr("Summary")}</h2>
              </div>
              <div className="grid gap-4 py-4 md:grid-cols-2">
                <DataLabel title={tr("Request ID")}>
                  {query.data?.fk_request_id ? (
                    <NoStyleA
                      className="!text-blue-700 !underline"
                      to={`/requests/${query.data?.fk_request_id}`}
                    >{`${tr("Request")} #${query.data?.fk_request_id}`}</NoStyleA>
                  ) : (
                    <span className="font-medium text-slate-400">
                      {tr("Unassigned")}
                    </span>
                  )}
                </DataLabel>
                <DataLabel title={tr("Project ID")}>
                  {query.data?.fk_project_id ? (
                    <NoStyleA
                      className="!text-blue-700 !underline"
                      to={`/projects/${query.data?.fk_project_id}`}
                    >{`${tr("Project")} #${query.data?.fk_project_id}`}</NoStyleA>
                  ) : (
                    <span className="font-medium text-slate-400">
                      {tr("Unassigned")}
                    </span>
                  )}
                </DataLabel>
                <DataLabel title={tr("PO Number")}>
                  <span className="text-gray-500">
                    {query.data?.po_number || tr("No PO Number")}
                  </span>
                </DataLabel>
                <DataLabel title={tr("Quickbooks Desktop")}>
                  {query.data?.qb_id && query.data?.qb_id !== "disabled" ? (
                    <span className="">{query.data?.qb_id}</span>
                  ) : (
                    <div className="flex flex-row">
                      <Switch
                        checked={!query.data?.qb_id}
                        onChange={(e) => updateQBSync.mutate(e)}
                        disabled={true}
                      />
                      <span className="mb-2 ml-2 font-medium text-gray-400">
                        {tr("Toggle Sync")}
                      </span>
                    </div>
                  )}
                  <OutlineButton 
                    onClick={handleClickAddToSyncLog}
                    disabled={true}
                  >
                    {tr("Add to Sync Log")}
                  </OutlineButton>
                </DataLabel>
                <DataLabel title={tr("Payment Options")}>
                  <div className="text-sm text-gray-500">
                    {tr("Payment options disabled for archived invoices")}
                  </div>
                </DataLabel>
                <DataLabel title={tr("Opened")}>
                  {query.data?.read_timestamp ? (
                    (() => {
                      const date = new Date(query.data?.read_timestamp);
                      return isNaN(date.getTime()) ? (
                        <span className="text-red-600">{tr("Invalid Date")}</span>
                      ) : (
                        format(date, "MMM dd, yyyy hh:mm aa", {
                          locale: locale,
                        })
                      );
                    })()
                  ) : (
                    <span className="text-red-600">{tr("Pending")}...</span>
                  )}
                </DataLabel>
                <div className="flex flex-col space-y-2">
                  <DataLabel title={tr("Created On")}>
                    <span className="text-gray-500">
                      {(() => {
                        if (!query.data?.creation_date) return tr("No date set");
                        const date = new Date(query.data.creation_date);
                        return isNaN(date.getTime()) ? tr("Invalid date") : format(date, "MMM dd, yyyy", { locale: locale });
                      })()}
                    </span>
                  </DataLabel>
                  {query.data?.due_date && (
                    <DataLabel title={tr("Due Date")}>
                      <div className="text-red-600">{query.data?.due_date}</div>
                    </DataLabel>
                  )}
                </div>
                <DataLabel title="Quickbooks Online">
                  <div>
                    <strong>QBO ID:</strong> {query.data?.qbol_id}
                  </div>
                  <div>
                    <strong>{tr("Sync Token")}:</strong>
                    {query.data?.qbol_sync_token}
                  </div>
                  <div>
                    <strong>{tr("Message")}:</strong> {query.data?.qbol_message}
                  </div>
                  <div>
                    <strong>{tr("Synced At")}:</strong>{" "}
                    {query.data?.qbol_synced_at
                      ? (() => {
                          const date = new Date(query.data?.qbol_synced_at);
                          return isNaN(date.getTime()) ? "Invalid Date" : date.toLocaleString();
                        })()
                      : "Not Synced"}
                  </div>
                  <br className="mt-2" />
                  <OutlineButton 
                    onClick={resyncInvoice}
                    disabled={true}
                  >
                    QBO Sync
                  </OutlineButton>
                </DataLabel>
              </div>
            </div>
            <CustomerContainer
              data={query.data?.customer}
              selected_address={query.data?.fk_address_id}
              size="lg"
            />
            <AppointmentCardMini
              id={
                query.data?.fk_request_id
                  ? query.data?.fk_request_id
                  : query.data?.fk_project_id
              }
              type={query.data?.fk_request_id ? "requests" : "projects"}
            />
            <PaymentContainer data={query.data?.payments} />
            <LineItemsContainer
              type="invoice"
              id={id}
              readOnly={true}
            />
            <div className="h-2" />
            {parseInt(id) > 0 && (
              <InvoiceProductionAllocation invoiceId={parseInt(id)} invoiceTotal={query.data?.actual_total / 100} />
            )}
            <div className="h-2" />
          </div>
        </SafeArea>
      </div>
    );
  }

  // Regular invoice view (not archived)
  return (
    <div className="divide-y">
      <SafeArea>
        <div className="flex justify-between py-4">
          <div>
            <BreadCrumbs link={[{ label: tr("Invoice"), to: "/invoices" }]} />
            <div className="text-baseline">
              <span className="text-3xl">
                {tr("Invoice")} #{query.data?.id}
              </span>
              <span className="mx-1 text-xl font-light text-slate-600">
                {tr("for")}
              </span>
              <NoStyleA to={`/customers/${query.data?.customer?.id}`}>
                <span className="text-xl font-medium text-slate-800">
                  {query.data?.customer?.name}
                </span>
              </NoStyleA>
              <Status color={statusColor || "128,128,128"} marginLeft="0.5rem">
                {tr(query.data?.status?.label || "Unknown")}
              </Status>
            </div>
          </div>
          <div className="flex space-x-2">
            {status === "open" && (
              <Button
                primary
                isLoading={sendInvoice.isLoading}
                onClick={() =>
                  modal.setModal({
                    component: (
                      <SendInvoiceModal
                        invoiceId={id}
                        type={query.data?.fk_request_id ? "request" : "project"}
                        typeId={
                          query.data?.fk_request_id?.toString() ||
                          query.data?.fk_project_id?.toString() ||
                          ""
                        }
                        emails={
                          query.data?.customer.email
                            ? [
                              query.data?.customer.email,
                              ...(query.data?.customer.additional_emails ||
                                []),
                            ]
                            : null
                        }
                      />
                    ),
                    label: tr("Send Invoice"),
                  })
                }
              >
                {tr("Send Invoice")}
              </Button>
            )}
            {status !== "open" && (
              <OutlineButton
                primary
                onClick={() =>
                  modal.setModal({
                    component: (
                      <SendInvoiceModal
                        invoiceId={id}
                        type={query.data?.fk_request_id ? "request" : "project"}
                        typeId={
                          query.data?.fk_request_id?.toString() ||
                          query.data?.fk_project_id?.toString() ||
                          ""
                        }
                        emails={
                          query.data?.customer.email
                            ? [
                              query.data?.customer.email,
                              ...(query.data?.customer.additional_emails ||
                                []),
                            ]
                            : null
                        }
                        isResend={true}
                      />
                    ),
                    label: tr("Resend Invoice"),
                  })
                }
              >
                {tr("Resend Invoice")}
              </OutlineButton>
            )}
            {/* {status === "paid" && (
              <OutlineButton
                primary
                onClick={handleResend}
              >
                {tr("Resend Invoice")}
              </OutlineButton>
            )} */}

            <Dropdown
              reference={
                <IconButton square>
                  <RiMore2Line />
                </IconButton>
              }
              data={[
                {
                  button: [
                    {
                      label: tr("Invoice PDF"),
                      onClick: () => {
                        modal.setModal({
                          component: (
                            <GenerateInvoicePDFModal
                              invoiceId={id}
                              emails={
                                query.data?.customer.email
                                  ? [query.data?.customer.email]
                                  : null
                              }
                            />
                          ),
                          label: tr("Invoice PDF"),
                        });
                      },
                    },
                    status === "paid" && {
                      label: tr("Send Paid Receipt"),
                      onClick: async () => {
                        const { data } = await fetchPdfExists();
                        if (data.exists) {
                          modal.setModal({
                            label: tr("Send Paid Receipt"),
                            component: (
                              <SendReceiptModal
                                invoiceId={id}
                                tr={tr}
                                customerEmails={customerEmails}
                                setCustomerEmails={setCustomerEmails}
                              />
                            ),
                          });
                        } else {
                          modal.setModal({
                            component: (
                              <GenerateInvoicePDFModal
                                invoiceId={id}
                                emails={
                                  query.data?.customer.email
                                    ? [query.data?.customer.email]
                                    : null
                                }
                                isReceipt={true}
                              />
                            ),
                            label: tr("Send Paid Receipt"),
                          });
                        }
                      }
                    },
                    status !== "paid" &&
                    query.data?.amount_due === 0 && {
                      label: tr("Mark as Paid"),
                      onClick: () => markInvoiceAsPaid.mutate(),
                    },
                    status !== "paid" && {
                      label: tr("Submit Payment"),
                      onClick: () =>
                        modal.setModal({
                          component: (
                            <NewPaymentModal
                              id={id}
                              type="invoice"
                              is_deposit={false}
                              amount_due={query.data?.amount_due}
                              customerId={query.data.customer?.id}
                            />
                          ),
                          label: tr("Submit Payment"),
                        }),
                    },
                    {
                      label: tr("Edit Tax/Discount/Fees"),
                      onClick: () =>
                        modal.setModal({
                          component: (
                            <TaxModal
                              id={query.data?.id}
                              data={query.data?.taxes}
                              type={"invoice"}
                            />
                          ),
                          label: tr("Tax"),
                        }),
                    },
                    status === "open" && {
                      label: tr("Mark Sent"),
                      onClick: () => {
                        modal.setModal({
                          component: <MarkInvoiceAsSentModal invoiceId={id} />,
                          label: tr("Mark Invoice as Sent"),
                        });
                      },
                    },
                    {
                      label: tr("Delete"),
                      onClick: () => {
                        // Check if there are any payments associated with the invoice first, if there are, show a modal with a warning, invoices with payments cannot be deleted
                        if (
                          Array.isArray(query.data?.payments) &&
                          query.data.payments.length > 0
                        ) {
                          modal.setModal({
                            component: (
                              <div className="p-6">
                                <div className="flex items-center mb-4">
                                  <div className="flex-shrink-0">
                                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                  </div>
                                  <div className="ml-3">
                                    <h3 className="text-lg font-medium text-gray-900">
                                      {tr("Cannot Delete Invoice with Associated Payments")}
                                    </h3>
                                  </div>
                                </div>
                                <div className="mt-4 flex justify-end">
                                  <Button
                                    onClick={() => modal.setModal()}
                                  >
                                    {tr("OK")}
                                  </Button>
                                </div>
                              </div>
                            ),
                            label: tr("Error"),
                          });
                        } else {
                          // Only show areYouSure if there are no payments
                          areYouSure.activate(() => {
                            deleteInvoice.mutate();
                          });
                        }
                      },
                    },
                  ],
                },
              ]}
            />
          </div>
        </div>
      </SafeArea>
      <SafeArea>
        <div className={`py-4 space-y-8`}>
          <div
            className="flex items-center p-6 py-10 space-x-8 rounded-lg border"
            style={{
              background: `rgba(${statusColor || "128,128,128"},0.15)`,
              color: `rgb(${statusColor || "128,128,128"})`,
            }}
          >
            <div className="flex flex-col">
              <span>{tr("Invoice Total")}</span>
              <span className="text-xl lg:text-5xl md:text-4xl">
                {dollarFormatter(query.data?.actual_total)}
              </span>
            </div>
            <div className="text-xl lg:text-5xl md:text-4xl">-</div>
            <div className="flex flex-col">
              <span>{tr("Paid")}</span>
              <span className="text-xl lg:text-5xl md:text-4xl">
                {dollarFormatter(query.data?.paid_total)}
              </span>
            </div>
            <div className="text-xl lg:text-5xl md:text-4xl">=</div>
            <div className="flex flex-col">
              <span>{tr("Due")}</span>
              <span className="text-xl lg:text-5xl md:text-4xl">
                {dollarFormatter(query.data?.amount_due)}
              </span>
            </div>
          </div>
          <div className="divide-y">
            <div className="py-4">
              <h2>{tr("Summary")}</h2>
            </div>
            <div className="grid gap-4 py-4 md:grid-cols-2">
              <div>
                <DataLabel title={tr("Request ID")}>
                  {query.data?.fk_request_id ? (
                    <NoStyleA
                      className="!text-blue-700 !underline"
                      to={`/requests/${query.data?.fk_request_id}`}
                    >{`${tr("Request")} #${query.data?.fk_request_id}`}</NoStyleA>
                  ) : (
                    <span className="font-medium text-slate-400">
                      {tr("Unassigned")}
                    </span>
                  )}
                </DataLabel>
                {query.data.request_description && (
                  <DataLabel title={tr("Request Description")} className="mt-1">
                    {query.data.request_description}
                  </DataLabel>
                )}
              </div>
              <DataLabel title={tr("Project ID")}>
                {query.data?.fk_project_id ? (
                  <NoStyleA
                    className="!text-blue-700 !underline"
                    to={`/projects/${query.data?.fk_project_id}`}
                  >{`${tr("Project")} #${query.data?.fk_project_id}`}</NoStyleA>
                ) : (
                  <span className="font-medium text-slate-400">
                    {tr("Unassigned")}
                  </span>
                )}
              </DataLabel>
              <DataLabel title={tr("PO Number")}>
                <InlineEdit
                  value={query.data?.po_number}
                  placeholder={tr("No PO Number")}
                  updateUrl={`company/invoice/${id}/po_number`}
                />
              </DataLabel>
              <DataLabel title={tr("Quickbooks Desktop")}>
                {query.data?.qb_id && query.data?.qb_id !== "disabled" ? (
                  <span className="">{query.data?.qb_id}</span>
                ) : (
                  <div className="flex flex-row">
                    <Switch
                      checked={!query.data?.qb_id}
                      onChange={(e) => updateQBSync.mutate(e)}
                    />
                    <span className="mb-2 ml-2 font-medium text-slate-400">
                      {tr("Toggle Sync")}
                    </span>
                  </div>
                )}
                <OutlineButton 
                  onClick={handleClickAddToSyncLog}
                >
                  {tr("Add to Sync Log")}
                </OutlineButton>
              </DataLabel>
              <DataLabel title={tr("Payment Options")}>
                <PayableOptions
                  is_payable={isPayable}
                  allow_card={allowCard}
                  allow_ach={allowAch}
                  updatePayOptions={handleUpdatePayOptions}
                />
              </DataLabel>
              <DataLabel title={tr("Opened")}>
                {query.data?.read_timestamp ? (
                  (() => {
                    const date = new Date(query.data?.read_timestamp);
                    return isNaN(date.getTime()) ? (
                      <span className="text-red-600">{tr("Invalid Date")}</span>
                    ) : (
                      format(date, "MMM dd, yyyy hh:mm aa", {
                        locale: locale,
                      })
                    );
                  })()
                ) : (
                  <span className="text-red-600">{tr("Pending")}...</span>
                )}
              </DataLabel>
              <div className="flex flex-col space-y-2">
                <DataLabel title={tr("Created On")}>
                  <InlineEdit
                    type="date"
                    value={(() => {
                      if (!query.data?.creation_date) return "";
                      const date = new Date(query.data.creation_date);
                      return isNaN(date.getTime()) ? "" : format(date, "yyyy-MM-dd");
                    })()}
                    placeholder={tr("No PO Number")}
                    onSubmit={(value: any) =>
                      new Date(value + " 0:00").toISOString()
                    }
                    updateUrl={`company/invoice/${id}/creation-date`}
                  />
                </DataLabel>
                {query.data?.due_date && (
                  <DataLabel title={tr("Due Date")}>
                    <div className="text-red-600">{query.data?.due_date}</div>
                  </DataLabel>
                )}
              </div>
              <DataLabel title="Quickbooks Online">
                <div>
                  <strong>QBO ID:</strong> {query.data?.qbol_id}
                </div>
                <div>
                  <strong>{tr("Sync Token")}:</strong>
                  {query.data?.qbol_sync_token}
                </div>
                <div>
                  <strong>{tr("Message")}:</strong> {query.data?.qbol_message}
                </div>
                <div>
                  <strong>{tr("Synced At")}:</strong>{" "}
                  {query.data?.qbol_synced_at
                    ? (() => {
                        const date = new Date(query.data?.qbol_synced_at);
                        return isNaN(date.getTime()) ? "Invalid Date" : date.toLocaleString();
                      })()
                    : "Not Synced"}
                </div>
                <br className="mt-2" />
                <OutlineButton onClick={resyncInvoice}>
                  QBO Sync
                </OutlineButton>
              </DataLabel>
            </div>
          </div>
          <CustomerContainer
            data={query.data?.customer}
            selected_address={query.data?.fk_address_id}
            size="lg"
          />
          <AppointmentCardMini
            id={
              query.data?.fk_request_id
                ? query.data?.fk_request_id
                : query.data?.fk_project_id
            }
            type={query.data?.fk_request_id ? "requests" : "projects"}
          />
          <PaymentContainer data={query.data?.payments} />
          <LineItemsContainer
            type="invoice"
            id={id}
            readOnly={query.data.status?.id !== 17}
          />
          <div className="h-2" />
          {parseInt(id) > 0 && (
            <InvoiceProductionAllocation invoiceId={parseInt(id)} invoiceTotal={query.data?.actual_total / 100} />
          )}
          <div className="h-2" />
        </div>
      </SafeArea>
    </div>
  );
}
